import { FiHeart, FiMinus, FiPlus } from 'react-icons/fi';
import { FaHeart } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { Item, FetchStatusType } from '../../../types/interfaces';
import { Button } from '../../../shared/ui/button';
import { ContactManagerButton } from '@shared/ui/contact-manager-button';
import { getCurrencySymbol } from '@shared/lib/currency';

export interface SelectedProductDetailProps {
  product: Item;
  pricesInRange: number | null | undefined;
  inputValue: number;
  setInputValue: (value: number) => void;
  handler: () => void;
  status: FetchStatusType;
  inCart: boolean;
  addingItemToCart: () => void;
  removeFromCart: (id: number) => void;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  // Цена за единицу паспорта/поверки и флаги «включены свичами выше».
  // Цена самого датчика сознательно не меняется (бизнес-правило: паспорт
  // и поверка — отдельная номенклатура, см. чат 21.05.2026 — Ринат).
  // Эти данные нужны только чтобы под основной ценой показать «Итого с
  // паспортом и поверкой: X», иначе пользователь ставит свичи и не
  // понимает, во сколько ему всё это в итоге обойдётся (вопрос Амира
  // в том же чате).
  passportUnitPrice?: number | null;
  verificationUnitPrice?: number | null;
  withPassport?: boolean;
  withVerification?: boolean;
}

const SelectedProductDetail = ({
  product,
  pricesInRange,
  inputValue,
  setInputValue,
  handler,
  status,
  inCart,
  addingItemToCart,
  removeFromCart,
  isFavorite,
  onToggleFavorite,
  passportUnitPrice,
  verificationUnitPrice,
  withPassport,
  withVerification,
}: SelectedProductDetailProps) => {
  const basePrice = product?.prices?.[0]?.price;
  const hasDiscount = pricesInRange != null && basePrice != null && pricesInRange < basePrice;
  const currency = useSelector((state: any) => state.cart?.currency);
  const currencySymbol = getCurrencySymbol(currency || 'rub');

  // Остатки/резервы и кнопка менеджеру — только для KZ (cart.currency
  // = 'kzt'). Для RU available = null → поведение как раньше, без
  // лимита и без блока остатков.
  const isKz = String(currency || '').toLowerCase() === 'kzt';
  const available =
    isKz && product?.available != null ? Number(product.available) : null;
  const maxQty = available != null ? Math.max(0, Math.min(available, 10000)) : 10000;
  const outOfStock = available != null && available <= 0;

  // Считаем «Итого с опциями» только когда хоть один свич реально
  // включён И мы знаем юнит-цену соответствующей опции. Цена самой
  // позиции — pricesInRange × qty (та же логика, что в корзине,
  // см. computeItemPrices в CartSummary). Округление через
  // Math.round — цены в БД целые, но findPriceForQuantity может вернуть
  // float из-за скидочных тиров.
  const passportTotal =
    withPassport && passportUnitPrice ? passportUnitPrice * inputValue : 0;
  const verificationTotal =
    withVerification && verificationUnitPrice
      ? verificationUnitPrice * inputValue
      : 0;
  const optionsTotal = passportTotal + verificationTotal;
  const showCombinedTotal = optionsTotal > 0 && pricesInRange != null;
  const combinedTotal = showCombinedTotal
    ? Math.round((pricesInRange! * inputValue + optionsTotal) * 100) / 100
    : null;

  // Подпись «Итого с паспортом и поверкой: X». Текст подстраивается
  // под то, какие именно опции включены (чтобы не врать в шапке).
  const totalLabel = (() => {
    if (withPassport && withVerification) return 'Итого с паспортом и поверкой';
    if (withPassport) return 'Итого с паспортом';
    if (withVerification) return 'Итого с поверкой';
    return 'Итого';
  })();

  return (
    <div className="flex items-start flex-wrap gap-5 mt-2">
      <div className="flex flex-col">
        <div className="flex items-baseline gap-2">
          {hasDiscount && (
            <span className="text-sm text-gray-400 line-through">{basePrice} {currencySymbol}</span>
          )}
          <span className="text-2xl font-semibold text-[#141414] whitespace-nowrap">
            {pricesInRange != null ? `${pricesInRange} ${currencySymbol}` : '—'}
          </span>
        </div>
        {showCombinedTotal && (
          <div className="text-xs text-gray-500 mt-1 whitespace-nowrap">
            {totalLabel}:{' '}
            <span className="text-[#141414] font-semibold">
              {combinedTotal} {currencySymbol}
            </span>
          </div>
        )}
        {available != null && (
          <div className="mt-1">
            <div
              className={`text-sm font-medium ${
                available > 0 ? 'text-[#2E7D32]' : 'text-[#e3282a]'
              }`}
            >
              {available > 0 ? `${available} шт. доступно` : 'Нет в наличии'}
            </div>
            {product?.stock != null && (
              <div className="text-xs text-gray-400">
                {Number(product.stock)} шт. есть на складе
              </div>
            )}
          </div>
        )}
      </div>
     
      {!(outOfStock && !inCart) && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setInputValue(Math.max(1, inputValue - 1))}
            disabled={inputValue <= 1}
            className="w-9 h-9 rounded-lg bg-[#F5F6FA] flex items-center justify-center text-[#141414] hover:text-[#e3282a] border-0 cursor-pointer disabled:opacity-30"
          >
            <FiMinus size={16} />
          </button>
          <input
            type="number"
            value={inputValue}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v)) setInputValue(Math.max(1, Math.min(maxQty, v)));
            }}
            onBlur={handler}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
            }}
            min={1}
            max={maxQty}
            className="w-14 h-9 text-center text-base font-medium text-[#141414] border border-gray-300 rounded-lg outline-none focus:border-[#e3282a] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <button
            onClick={() => setInputValue(Math.min(maxQty, inputValue + 1))}
            disabled={inputValue >= maxQty}
            className="w-9 h-9 rounded-lg bg-[#F5F6FA] flex items-center justify-center text-[#141414] hover:text-[#e3282a] border-0 cursor-pointer disabled:opacity-30"
          >
            <FiPlus size={16} />
          </button>
        </div>
      )}

      <div className="flex items-center gap-5 flex-1 min-w-[350px]">
        <div className="flex-1 min-w-[220px] h-12">
          {outOfStock && !inCart ? (
            <ContactManagerButton itemId={product.id} />
          ) : (
            <Button
              status={status}
              onClick={() => (inCart ? removeFromCart(product?.id) : addingItemToCart())}
              type="button"
              isWhite={inCart}
              disabled={!inCart && pricesInRange == null}
            >
              {inCart ? 'Удалить из корзины' : 'Добавить в корзину'}
            </Button>
          )}
        </div>

        <button
          onClick={onToggleFavorite}
          type="button"
          className="w-12 h-12 rounded-full bg-[#F5F6FA] flex items-center justify-center border-0 cursor-pointer shrink-0 transition-colors hover:bg-[#FCE9E9]"
        >
          {isFavorite
            ? <FaHeart color="#e3282a" size={20} />
            : <FiHeart size={20} color="#e3282a" />
          }
        </button>
      </div>
    </div>
  );
};

export default SelectedProductDetail;
